import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Empty, Spin, Input, Typography, Tooltip } from 'antd';
import PaginationTable from '../../../../shared/PaginationTable';
import Button from '../../../../shared/globalStyling/Button';
import {
  SearchOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { useBreakpoints } from '../../../../shared/globalStyling/screenSizes';
import { dateFormat, pagination } from '../../../../ux/Tasks/configs/designTokens';

const { Text } = Typography;

// ─── Expanded row: locations list (shared by table and card views) ────
const ExpandedLocations = ({ record }) => {
  const locations = record.geoTargets
    ? record.geoTargets.filter((t) => t.location)
    : [];

  if (!locations.length) {
    return (
      <div className="targeting-audience__expand-empty">No locations</div>
    );
  }

  return (
    <div className="targeting-audience__expand">
      {locations.map((target) => (
        <div key={target.id} className="targeting-audience__expand-item">
          <EnvironmentOutlined className="targeting-audience__expand-icon" />
          <span className="targeting-audience__expand-name">{target.name}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Card view (tablet / mobile) ─────────────────────────────────────
const AudienceCardList = ({
  data,
  selectedCardKeys,
  onCardSelect,
  amplify,
  expandedCardId,
  onCardExpand,
}) => (
  <div className="targeting-audience__card-list">
    {data.map((audience) => {
      const locationCount = audience.geoTargets
        ? audience.geoTargets.filter((t) => t.location).length
        : 0;
      const isSelected = selectedCardKeys.includes(audience.id);
      const isExpanded = expandedCardId === audience.id;

      return (
        <div key={audience.id} className={`targeting-audience__card-wrap${isSelected ? ' targeting-audience__card-wrap--selected' : ''}`}>
          <div
            className="targeting-audience__card"
            onClick={() => onCardSelect(audience.id)}
          >
            <div className="targeting-audience__card-header">
              <span className="targeting-audience__card-name">{audience.name}</span>
              {audience.isAmplifyDefault && amplify && (
                <span className="targeting-audience__card-badge targeting-audience__card-badge--amplify">
                  Amplify Default
                </span>
              )}
            </div>
            <div className="targeting-audience__card-meta">
              <span className="targeting-audience__card-stat">
                <EnvironmentOutlined /> {locationCount} location{locationCount !== 1 ? 's' : ''}
              </span>
              <span className="targeting-audience__card-stat">
                <CalendarOutlined /> {moment(audience.createdAt).format(dateFormat.dateOnly)}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="targeting-audience__card-expand"
            onClick={() => onCardExpand(isExpanded ? null : audience.id)}
          >
            {isExpanded ? 'Hide locations' : `View ${locationCount} location${locationCount !== 1 ? 's' : ''}`}
            <span className={`targeting-audience__card-chevron${isExpanded ? ' targeting-audience__card-chevron--open' : ''}`}>
              &#9662;
            </span>
          </button>
          {isExpanded && <ExpandedLocations record={audience} />}
        </div>
      );
    })}
  </div>
);

export default function TargetingLocationAudiencesList({
  toolbarContainer,
  targetingAudiences,
  setSelectedRows,
  setAudienceAmplifyDefault,
  amplify,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pageSize, setPageSize] = useState(pagination.defaultPageSize);
  const [selectedCardKeys, setSelectedCardKeys] = useState([]);
  const [expandedCardId, setExpandedCardId] = useState(null);

  const { isTablet } = useBreakpoints();

  let columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, record) => (
        <div className="targeting-audience__name-cell targeting-audience__name-cell--stacked">
          <Tooltip title={name}>
            <span className="targeting-audience__name-text">{name}</span>
          </Tooltip>
          {record.isAmplifyDefault && amplify && (
            <span className="targeting-audience__amplify-pill">Amplify Default</span>
          )}
        </div>
      ),
    },
    {
      title: 'Locations',
      dataIndex: 'geoTargets',
      key: 'geoTargets',
      align: 'left',
      width: 120,
      render: (geoTargets, record) => {
        const count = record.geoTargets
          ? record.geoTargets.filter((t) => t.location).length
          : 0;
        return (
          <span className="targeting-audience__location-badge">
            {count} location{count !== 1 ? 's' : ''}
          </span>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (val) => (
        <span className="targeting-audience__date">{moment(val).format(dateFormat.dateOnly)}</span>
      ),
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 140,
      render: (val) => (
        <span className="targeting-audience__date">{moment(val).format(dateFormat.dateOnly)}</span>
      ),
      sorter: (a, b) => moment(a.updatedAt).unix() - moment(b.updatedAt).unix(),
    },
    {
      title: 'Options',
      dataIndex: 'amplifySelect',
      key: 'amplifySelect',
      width: 180,
      align: 'center',
      render: (val, record) => (
        <Button
          type="primary"
          size="small"
          disabled={record.isAmplifyDefault}
          className="targeting-audience__amplify-btn"
          onClick={(e) => {
            e.stopPropagation();
            selectDefaultCreative(record);
          }}
        >
          Select as Amplify Default
        </Button>
      ),
    },
  ];

  const selectDefaultCreative = async (record) => {
    setLoading(true);
    await setAudienceAmplifyDefault(record);
    setLoading(false);
  };

  // hide column of amplify
  if (!amplify) {
    columns = columns.filter((col) => col.key !== 'amplifySelect');
  }

  // Filtering logic
  const filteredData = targetingAudiences
    .filter((audience) =>
      audience.name.toLowerCase().includes(searchText.toLowerCase())
    )
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const handleCardSelect = (audienceId) => {
    setSelectedCardKeys((prev) => {
      const next = prev.includes(audienceId)
        ? prev.filter((id) => id !== audienceId)
        : [...prev, audienceId];

      const selectedAudiences = targetingAudiences.filter((a) => next.includes(a.id));
      setSelectedRows(selectedAudiences);

      return next;
    });
  };

  // ─── Expandable row for table view ──────────────────────────────────
  const expandedRowRender = (record) => <ExpandedLocations record={record} />;

  if (!targetingAudiences || !targetingAudiences.length) {
    return <Empty />;
  }

  return (
    <div>
      {/* Search bar — portaled into tab bar */}
      {toolbarContainer && ReactDOM.createPortal(
        <div className="targeting-search">
          <Input
            placeholder="Search audiences"
            value={searchText}
            allowClear
            prefix={<SearchOutlined />}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>,
        toolbarContainer
      )}

      {/* Table (desktop/laptop) or Cards (tablet/mobile) */}
      {isTablet ? (
        <AudienceCardList
          data={filteredData}
          selectedCardKeys={selectedCardKeys}
          onCardSelect={handleCardSelect}
          amplify={amplify}
          expandedCardId={expandedCardId}
          onCardExpand={setExpandedCardId}
        />
      ) : (
        <div className="targeting-audience__table">
          <Spin tip="Loading..." spinning={isLoading}>
            <PaginationTable
              columns={columns}
              data={filteredData}
              bordered={false}
              paginationSize={'default'}
              scroll={{ x: 500 }}
              size={'small'}
              total={filteredData && filteredData.length}
              current={currentPage}
              pageSize={pageSize}
              rowSelection={{
                type: 'checkbox',
                getCheckboxProps: (record) => ({}),
                onChange: (selectedRowKeys, selectedRows) => {
                  setSelectedRows(selectedRows);
                },
              }}
              onChange={(paginationObject) => {
                setPageSize(paginationObject.pageSize);
                setCurrentPage(paginationObject.current);
              }}
              rowKey="id"
              expandedRowRender={expandedRowRender}
            />
          </Spin>
        </div>
      )}

    </div>
  );
}
